/**
 * Source: fetching half of the webshop adapter.
 *
 * `webshop.js` is the pure shape-translation half and stays testable with no
 * bench; this is where the network lives. Together they are "the adapter" —
 * the only layer allowed to know what a webshop row looks like.
 *
 * THE CONSOLIDATION: the five decoration calls that today run as five
 * independent scripts — each with its own cache, in-flight flag, setTimeout
 * (600/650/800/1000ms) and MutationObserver — are issued here as ONE parallel
 * batch alongside the listing, and resolved before a single card renders.
 *
 * Decoration failure is non-fatal by design. If badges 500, cards render
 * without badges. The current architecture gets this property by accident
 * (five unrelated scripts); here it is deliberate.
 */

import { adaptItems, normalizeRoute } from './webshop.js';
import { cacheKey, getCachedListing, setCachedListing, stripDynamicFields } from './catalog_cache.js';
import { getSwatchColorOverrides } from '../swatch_color_overrides.js';
import { getAttributeIcons } from '../attribute_icons.js';

const LISTING_METHOD = 'webshop.webshop.api.get_product_filter_data';
const M_LIVE_FIELDS = 'raftor_webhq.catalog.api.get_item_live_fields';
const M_VARIANTS = 'raftor_webhq.catalog.api.get_variant_attributes_for_routes';
const M_BADGES = 'raftor_webhq.catalog.api.get_item_badges';
const M_OFFERS = 'raftor_webhq.catalog.api.get_item_offers';
const M_DISCOUNTS = 'raftor_webhq.catalog.api.get_consumer_discounts';
const M_RATING = 'raftor_webhq.catalog.api.get_item_ratings';
// brand now arrives inline on the listing row (StableSortProductQuery widens
// the field list), so get_item_brands is no longer called — one round-trip
// fewer per listing. The endpoint itself stays for any other consumer.
const M_MEDIA = 'raftor_webhq.catalog.api.get_item_media';

/**
 * Methods that have already failed hard (typically because the app providing
 * them is not installed on this site). Retrying them once per listing page
 * would mean five doomed requests and five error dialogs every time.
 */
const unavailableMethods = new Set();

/**
 * Promise-wrapped frappe.call that is GUARANTEED to settle.
 *
 * frappe.call can reject without invoking either `callback` or `error` — a
 * server-side exception is one such case. If that happens inside a
 * Promise.all, the whole render stays pending forever and not a single card
 * appears. Settling via `.always()` (and a last-resort timeout when the
 * returned object is not a jQuery promise) is what keeps a failed decoration
 * a missing badge rather than a blank page.
 */
function call(method, args, opts) {
	const optional = Boolean(opts && opts.optional);
	if (optional && unavailableMethods.has(method)) return Promise.resolve(null);

	return new Promise((resolve) => {
		let settled = false;
		const done = (value) => {
			if (settled) return;
			settled = true;
			resolve(value);
		};
		const giveUp = () => {
			if (optional) unavailableMethods.add(method);
			done(null);
		};

		if (typeof frappe === 'undefined' || !frappe.call) {
			done(null);
			return;
		}

		let req;
		try {
			req = frappe.call({ method, args, callback: (r) => done(r && r.message), error: giveUp });
		} catch (err) {
			giveUp();
			return;
		}

		// The net must be deferred, not immediate. frappe.call returns a jQuery
		// deferred whose `always` handlers run BEFORE it invokes opts.callback
		// — verified in the browser: the observed order is `always` then
		// `callback`. Resolving synchronously here would therefore discard
		// every successful response and silently render an empty grid.
		// setTimeout(0) puts the fallback after the callback in every case.
		const armFallback = (delay) =>
			setTimeout(() => {
				if (!settled) giveUp();
			}, delay);

		if (req && typeof req.always === 'function') {
			req.always(() => armFallback(0));
		} else {
			armFallback(15000);
		}
	});
}

/** A decoration call that fails yields null instead of taking the page down. */
function optional(method, args) {
	return call(method, args, { optional: true });
}

/**
 * RaftorConnectivity (raftor_webhq/public/js/connectivity.js) loads sitewide
 * via web_include_js, but websitehq must not hard-depend on raftor_webhq
 * being installed — same rule fetchDecorations already applies to every
 * decoration endpoint below, just for a global instead of an API call.
 */
function isSlowConnection() {
	return Boolean(
		typeof window !== 'undefined' && window.RaftorConnectivity && window.RaftorConnectivity.isSlow()
	);
}

function badgeTone(type) {
	return String(type || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

/**
 * One parallel decoration batch (variants/badges/offers/discounts/media/
 * ratings), keyed back to routes (INVARIANT 1) — the shared implementation
 * every Source uses, not just WebshopSource's own listing/adaptRaw. Product
 * cards are meant to look and behave identically wherever they render (see
 * PRODUCT_CARD_PLAN.md's whole premise) — a card with no swatches/sizes/
 * badges on the wishlist or a recommendation rail because THAT source never
 * fetched them is the same inconsistency bug class as a theming gap, just
 * in the data layer instead of CSS. Exported standalone (not just this
 * class's own private method) so RecommendationSource and WishlistSource
 * call the exact same batch rather than each growing their own partial
 * copy that drifts from this one over time.
 */
export async function fetchDecorations(routes, itemCodes, items, cartStore) {
	const uniqueRoutes = [...new Set(routes)];
	const uniqueCodes = [...new Set(itemCodes)];

	// Every one of these is optional: they are served by raftor_webhq, which
	// websitehq must not hard-depend on. With it absent the cards still
	// render — just without badges, offers, swatches, brand or discount.
	//
	// On a slow/2G connection, offers and ratings — the two purely decorative
	// calls a purchase decision doesn't depend on — are skipped rather than
	// fired. Cards already render correctly with null offers/rating (see
	// byRoute below), so this is a pure latency/data-usage win, not a
	// degraded card; it's just a staged reveal that never arrives on this
	// load.
	const slow = isSlowConnection();
	const [variants, badges, offers, discounts, media, ratings] = await Promise.all([
		uniqueRoutes.length ? optional(M_VARIANTS, { routes: uniqueRoutes }) : null,
		uniqueCodes.length ? optional(M_BADGES, { item_codes: uniqueCodes }) : null,
		uniqueCodes.length && !slow ? optional(M_OFFERS, { item_codes: uniqueCodes }) : null,
		uniqueCodes.length ? optional(M_DISCOUNTS, { item_codes: uniqueCodes }) : null,
		uniqueCodes.length ? optional(M_MEDIA, { item_codes: uniqueCodes }) : null,
		uniqueCodes.length && !slow ? optional(M_RATING, { item_codes: uniqueCodes }) : null,
	]);

	const byRoute = {};
	for (const raw of items) {
		const route = normalizeRoute(raw.route);
		if (!route) continue;
		const code = raw.item_code || raw.name;

		byRoute[route] = {
			// get_variant_attributes_for_routes STRIPS the leading slash from
			// its response keys, while our route identity keeps it (see
			// normalizeRoute — INVARIANT 1). Look up both spellings or every
			// product silently comes back with no variants at all.
			variantAttributes:
				(variants && (variants[route] || variants[route.replace(/^\/+/, '')])) || null,
			badges: ((badges && badges[code]) || []).map((b) => ({
				// A Notice carries its own text (e.g. "Unverified"); the other types are their own label.
				label: b.label || b.badge_type,
				tone: badgeTone(b.badge_type),
				// get_item_badges already returns validity dates and has
				// already filtered to active rows — carry the end date so a
				// time-limited badge ("Sale") can drive a countdown without
				// any new field, endpoint or round-trip.
				endsAt: b.valid_upto || null,
			})),
			offers: ((offers && offers[code]) || []).map((o) => ({
				title: o.offer_title || '',
				subtitle: o.offer_subtitle || '',
				// get_item_offers already filtered to currently-active rows -
				// this is display-only, telling the shopper when THIS offer
				// stops, same role badges.endsAt plays for the Sale countdown.
				endDate: o.end_date || null,
			})),
			consumerDiscount: (discounts && discounts[code]) != null ? discounts[code] : null,
			// brand is on the listing row itself now; nothing to merge here.
			gallery: ((media && media[code]) || {}).gallery || null,
			rating: (ratings && ratings[code]) || null,
			cartQty: cartStore ? cartStore.getQty(code) : 0,
		};
	}
	return byRoute;
}

/** Derives the (route, item_code) pairs fetchDecorations needs from raw
 * listing rows — shared so every Source extracts them identically. */
export function routesAndCodes(items) {
	const routes = [];
	const itemCodes = [];
	for (const raw of items || []) {
		const route = normalizeRoute(raw.route);
		if (route) routes.push(route);
		const code = raw.item_code || raw.name;
		if (code) itemCodes.push(code);
	}
	return { routes, itemCodes };
}

/**
 * Patches live price/discount/stock onto a cached (static-fields-only)
 * listing response. Returns null — never throws — if the live call fails,
 * so the caller can fall back to a full fresh listing instead of rendering
 * cards with no price.
 */
async function mergeLiveFields(listing) {
	const items = listing.items || [];
	const itemCodes = [...new Set(items.map((it) => it.item_code || it.name).filter(Boolean))];
	if (!itemCodes.length) return listing;

	const liveByCode = await call(M_LIVE_FIELDS, { item_codes: itemCodes });
	if (!liveByCode) return null;

	return Object.assign({}, listing, {
		items: items.map((item) =>
			Object.assign({}, item, liveByCode[item.item_code || item.name] || {})
		),
	});
}

export class WebshopSource {
	/**
	 * @param {object} [opts]
	 * @param {CartStore} [opts.cartStore] supplies per-item quantity at map time
	 */
	constructor(opts) {
		this.cartStore = (opts || {}).cartStore || null;
	}

	/**
	 * @param {object} query { start, filters }
	 * @returns {Promise<{models, settings, pagination}>}
	 */
	async fetch(query) {
		const q = query || {};
		const args = {
			query_args: Object.assign({ start: q.start || 0, from_filters: 0 }, q.filters || {}),
		};
		const key = cacheKey(args);

		const fetchFresh = () =>
			call(LISTING_METHOD, args).then((fresh) => {
				// Only the static half is written back — see catalog_cache.js's
				// DYNAMIC_FIELDS. The fresh response itself (with price/stock
				// intact) is what THIS load renders; only the next visit reads
				// the cache.
				if (fresh) setCachedListing(key, stripDynamicFields(fresh));
				return fresh;
			});

		// Stale-while-revalidate, but only for the static half of a row (name/
		// image/route/brand). Price, discount and stock are never read from
		// this cache — see catalog_cache.js's header comment for why — so a
		// cache hit still costs one network round trip, just a far cheaper
		// one: get_item_live_fields looks up a known item_code list directly
		// instead of re-running the filtered/sorted/counted listing query.
		//
		// A full background refetch of the listing itself also fires (fire-
		// and-forget) so next visit's cached shell stays current too. Already-
		// rendered cards are never mutated in place — Paginator appends pages
		// by identity (INVARIANT 1) and has no mechanism for patching cards
		// after the fact — so that refetch only benefits the *next* visit,
		// same as before.
		const cached = await getCachedListing(key);
		let listing = null;
		if (cached && Array.isArray(cached.items) && cached.items.length) {
			listing = await mergeLiveFields(cached);
			if (listing) fetchFresh();
		}
		if (!listing) {
			listing = await fetchFresh();
		}

		if (!listing || !Array.isArray(listing.items)) {
			return { models: [], settings: {}, pagination: emptyPagination(q.start || 0) };
		}

		const items = listing.items;
		const settings = listing.settings || {};

		const { routes, itemCodes } = routesAndCodes(items);
		const [decorations, swatchColorOverrides, attributeIcons] = await Promise.all([
			fetchDecorations(routes, itemCodes, items, this.cartStore),
			getSwatchColorOverrides(),
			getAttributeIcons(),
		]);
		settings.swatchColorOverrides = swatchColorOverrides;
		settings.attributeIcons = attributeIcons;
		const models = adaptItems(items, settings, decorations);

		return { models, settings, pagination: readPagination(listing, q.start || 0) };
	}

	/**
	 * Adapt rows that were fetched by someone else — core's ProductView hands
	 * its first page straight to the grid constructor. Runs the same decoration
	 * batch so a seeded page is indistinguishable from one we fetched, and
	 * avoids re-requesting a listing the page already has.
	 */
	async adaptRaw(items, settings) {
		const rows = items || [];
		if (!rows.length) return [];

		const { routes, itemCodes } = routesAndCodes(rows);
		const [decorations, swatchColorOverrides, attributeIcons] = await Promise.all([
			fetchDecorations(routes, itemCodes, rows, this.cartStore),
			getSwatchColorOverrides(),
			getAttributeIcons(),
		]);
		const mergedSettings = Object.assign({}, settings || {}, { swatchColorOverrides, attributeIcons });
		return adaptItems(rows, mergedSettings, decorations);
	}
}

/* ── pagination contract ───────────────────────────────────────────────────
 * raftor_webhq's get_products returns the full contract on EVERY response,
 * including empty ones (_empty_listing_response). Read it straight; do not
 * infer from data shape.
 */

function emptyPagination(start) {
	return { nextStart: start, hasMore: false, totalCount: 0, countReliable: true, pageLength: null };
}

export function readPagination(message, start) {
	const totalCount = Number(message.items_count);
	const nextStart = Number(message.next_start);
	const pageLength = Number(message.settings && message.settings.products_per_page);
	const rawHasMore = message.has_more;

	return {
		nextStart: Number.isFinite(nextStart) && nextStart >= 0 ? nextStart : start,
		// undefined means an older server that predates the signal — the
		// paginator falls back to counts, then to a short-page heuristic.
		hasMore: rawHasMore === undefined || rawHasMore === null ? null : Boolean(Number(rawHasMore)),
		totalCount: Number.isFinite(totalCount) && totalCount >= 0 ? totalCount : null,
		countReliable: Number(message.count_reliable) !== 0,
		pageLength: Number.isFinite(pageLength) && pageLength > 0 ? pageLength : null,
		returnedCount: Array.isArray(message.items) ? message.items.length : 0,
	};
}
